/**
 * WSOLA — waveform-similarity overlap-add time stretching.
 *
 * ── What it's for ──
 *
 * The pump's read cursor advances by `speed × fileRate / contextRate`, which
 * quietly does two jobs at once: rate conversion (mandatory, pitch-neutral) and
 * varispeed (which shifts pitch by exactly `speed`). Pitch preservation is what
 * you get by separating them — stretch the file-rate stream by `α = 1/speed`
 * first, and the interpolator is left doing nothing but `fileRate/contextRate`.
 *
 * The consequences are worth stating, because they're why it goes here and not
 * after the interpolator:
 *
 * - **The interpolation kernels never learn this feature exists.** They still
 *   see a buffer and a fractional position. Pitch preservation composes with
 *   every one of them for free, and no kernel needs a variant.
 * - **The transport arithmetic is untouched.** Stretching by `1/speed` while
 *   the file is consumed at `speed` cancels exactly, so the ideal input position
 *   still advances at the rate `#rebaseTransport` assumes. No resync loop.
 * - **Doing it the other way round costs a second resampler.** Pitch-shifting
 *   the *output* means stretching by `speed` and then resampling by `1/speed` to
 *   undo it — the same WSOLA plus another interpolation pass, at the context
 *   rate rather than the (usually lower) file rate.
 *
 * ── The algorithm ──
 *
 * Overlap-add frames of the input at a fixed *synthesis* hop, taking them from
 * an *analysis* position that advances at `hop × speed`. That alone is OLA and
 * it sounds terrible: consecutive frames land at arbitrary phase and the
 * periodicity of voiced speech tears apart. WSOLA's fix is to let the analysis
 * position slide within a small search window, choosing the offset whose
 * waveform best continues what was already emitted — hence "waveform
 * similarity". The search window has to span at least one pitch period, which
 * for a low male voice near 85 Hz means ~12 ms.
 *
 * Correlation is against the *natural continuation* of the previous frame —
 * raw input either side, no window involved — rather than against the windowed
 * output tail, which would bias the match by the taper.
 *
 * At `α = 1` the search finds offset 0 outright (Cauchy–Schwarz: nothing
 * correlates with a signal better than itself), and periodic-Hann overlap-add at
 * half-frame hop sums to exactly 1. So unity speed passes through untouched, and
 * leaving pitch preservation switched on costs nothing at 1×.
 *
 * ── What the caller handles ──
 *
 * **Reverse** is not a special case here: the pump traverses the source backwards
 * and runs this forwards over it, which is precisely what reverse playback is.
 *
 * **Extreme ratios** are handled by the pump clamping the stretch factor, so
 * quality degrades into *partial* pitch correction rather than warbling — the
 * clamp is continuous, so there is no boundary to click across.
 *
 * ── The one remaining limitation ──
 *
 * **Cross-track alignment.** Each pump searches independently, so two microphones
 * on one speaker can settle on offsets up to a search window apart and comb-filter
 * against each other. It only bites when two or more tracks are *audible at once*
 * at an off speed; a single track, or tracks auditioned one at a time, is exact.
 * The fix is one set of offsets shared across every track, which really wants one
 * pump with N output channels rather than N pumps — see DISPLAY_PLAYBACK_PLAN.md.
 */

/**
 * Periodic Hann window.
 *
 * Periodic rather than symmetric (`/ length`, not `/ (length - 1)`) because that
 * is the form whose overlap at half-frame hop sums to exactly unity —
 * `hann[i] + hann[i + length/2] === 1` — so the overlap-add needs no
 * normalisation pass.
 */
export function hannWindow(length) {
  const window = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / length);
  }
  return window;
}

/**
 * The offset whose waveform best continues `template`.
 *
 * `candidates` holds the input over `[base - search, base + search + hop)`, so
 * lag `d` starts at index `d + search`. Returns `d` in `[-search, search]`.
 *
 * Ties go to the smallest shift, which is what keeps unity speed exact: at
 * `alpha = 1` the ideal position already *is* the natural continuation, and
 * nothing correlates with a signal better than itself.
 *
 * `decimation` trades search cost against nothing much — see the comment in the
 * body. Pass 1 for an exhaustive search.
 */
export function bestOffset(candidates, template, hop, search, decimation = 4) {
  // Coarse pass on a decimated view, then refine. Brute force over every lag is
  // ~14M multiply-adds per second per track at 24 kHz; this is about a tenth of
  // that, because the work falls by the square of the decimation — the lag grid
  // and the correlation length both shrink.
  //
  // Safe for speech: at 24 kHz, decimating by 4 still leaves 6 kHz of bandwidth,
  // far above the pitch periodicity the search is actually locking to. The
  // refine pass then recovers the exact lag, so the answer matches brute force.
  const step = Math.max(1, Math.floor(decimation));

  let bestScore = -Infinity;
  let coarse = 0;

  for (let delta = -search; delta <= search; delta += step) {
    const score = correlate(candidates, template, hop, delta + search, step);
    if (score > bestScore || (score === bestScore && Math.abs(delta) < Math.abs(coarse))) {
      bestScore = score;
      coarse = delta;
    }
  }

  if (step === 1) return coarse;

  // Refine at full resolution, within one coarse step either side.
  const from = Math.max(-search, coarse - step);
  const to = Math.min(search, coarse + step);

  bestScore = -Infinity;
  let best = coarse;

  for (let delta = from; delta <= to; delta++) {
    const score = correlate(candidates, template, hop, delta + search, 1);
    if (score > bestScore || (score === bestScore && Math.abs(delta) < Math.abs(best))) {
      bestScore = score;
      best = delta;
    }
  }

  return best;
}

/**
 * Normalised cross-correlation of one candidate window against the template.
 *
 * Normalised by the candidate's own energy: the template is fixed across lags,
 * so its norm wouldn't change the ranking, but the candidate's energy varies and
 * without it the search finds the loudest window rather than the best-matching
 * one.
 *
 * `step` samples the window rather than the lag — used by the coarse pass.
 */
export function correlate(candidates, template, hop, start, step) {
  let dot = 0;
  let energy = 0;

  for (let i = 0; i < hop; i += step) {
    const sample = candidates[start + i];
    dot += sample * template[i];
    energy += sample * sample;
  }

  return dot / Math.sqrt(energy + 1e-12);
}

/**
 * Frame geometry for a file rate, in source samples.
 *
 * 30 ms frames: long enough to hold a couple of pitch periods of low speech,
 * short enough that the analysis position doesn't visibly lag a transient. The
 * 12 ms search is the binding constraint — it has to cover a full pitch period
 * of the lowest voice we expect, and 1/85 Hz is 11.8 ms.
 */
export function stretchGeometry(fileRate) {
  let frame = Math.round(0.03 * fileRate);
  if (frame % 2 === 1) frame += 1; // hop is exactly half

  return {
    frame,
    hop: frame / 2,
    search: Math.max(1, Math.round(0.012 * fileRate)),
  };
}

/**
 * Everything the worklet needs, injected together.
 *
 * `correlate` is in here because `bestOffset` calls it: they reach the audio
 * thread as stringified source, so a helper left out of this set is simply not
 * defined there, and the failure is a ReferenceError on the first stretched
 * sample rather than anything a linter would catch.
 */
export const TIMESTRETCH_FUNCTIONS = { hannWindow, correlate, bestOffset, stretchGeometry };
